import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useApiCaller } from '../../utils/ApiUtilities';
import {
    getPublicGradeUrl,
    getPublicQuizQuestionsUrl,
    getQuickSubmissionUrl,
    getQuickCountUrl,
    getAnonLimitsUrl,
} from '../../utils/UrlUtilities';
import { constructPublicGradePayload, constructQuickSubmissionPayload } from '../../utils/PayloadUtils';
import { appendAnonymousQuiz, anonymousToday, getAnonymousQuizCountToday, appendRecentQuiz } from '../../utils/AnonymousProgress';
import QuizQuestionCard from '../QuizQuestionCard';
import { buildSelectedOptionsJson } from '../../utils/BasicUtilities';
import { QUICK_QUIZ_DAILY_LIMIT } from '../../utils/Constants';
import { limitReachedMessage } from '../../utils/gatingMessages';
import useEntitlement from '../../utils/useEntitlement';
import { useUser } from '../../contexts/UserContext';
import UpgradeNudge from '../UpgradeNudge';
import LandingReportCard from './LandingReportCard';


const QUIZ_COUNT = 15;

function LandingQuickQuiz({ level, bcsTerm = '', division = '', unit = '', onJumpToPractice, onActivity, restartSignal = 0 }) {
    const CallApi = useApiCaller();
    const { userState, anonImportTick } = useUser();
    const { isPremium } = useEntitlement();
    const isLoggedIn = !!userState?.isLoggedIn;
    const [phase, setPhase] = useState('idle'); // idle | in_progress | results
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [quizCountToday, setQuizCountToday] = useState(getAnonymousQuizCountToday());
    // Collapse the question list (keeping all selections) so the sections below
    // (notice board, etc.) are reachable without scrolling past every question.
    const [collapsed, setCollapsed] = useState(false);
    // Logged-in users have a server-enforced, plan-based daily cap; track today's count + limit.
    const [loggedInCountToday, setLoggedInCountToday] = useState(0);
    const [loggedInLimit, setLoggedInLimit] = useState(QUICK_QUIZ_DAILY_LIMIT);
    // Anonymous cap comes from the server's tunable LIMIT_QUICK_ANON (fallback to the constant).
    const [anonLimit, setAnonLimit] = useState(QUICK_QUIZ_DAILY_LIMIT);

    // Daily quick-quiz allowance for the current viewer. Anonymous users are capped
    // client-side; logged-in users use the plan-based limit from the server.
    const dailyLimit = isLoggedIn ? loggedInLimit : anonLimit;
    const usedToday = isLoggedIn ? loggedInCountToday : quizCountToday;
    const limitReached = dailyLimit != null && usedToday >= dailyLimit;
    const remaining = dailyLimit == null ? null : Math.max(0, dailyLimit - usedToday);

    // Anonymous users: pull the tunable anon cap so LIMIT_QUICK_ANON is respected.
    useEffect(() => {
        if (isLoggedIn) return;
        let cancelled = false;
        (async () => {
            const res = await CallApi({ url: getAnonLimitsUrl(), method: 'GET', token: false });
            if (!cancelled && res.success && res.data?.quick != null) setAnonLimit(res.data.quick);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // Pull today's logged-in quick-quiz count + plan limit so the cap is enforced +
    // displayed. Re-runs after login's anon-history import (anonImportTick) so merged
    // pre-login attempts are reflected without a manual reload. Count is the combined
    // Daily Quick Quiz bucket (homepage + Daily Challenge).
    useEffect(() => {
        if (!isLoggedIn) return;
        let cancelled = false;
        (async () => {
            const res = await CallApi({ url: getQuickCountUrl(), method: 'GET', token: true });
            if (!cancelled && res.success) {
                setLoggedInCountToday(res.data?.count ?? 0);
                setLoggedInLimit(res.data?.limit ?? null);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, anonImportTick]);

    // When the level or BCS term changes, reset back to the start screen so the
    // user explicitly starts a fresh quiz with a question set for the new
    // selection. (Switching tabs does NOT remount this component, so the set
    // stays put otherwise — it only changes on reload or a new selection.)
    useEffect(() => {
        setPhase('idle');
        setQuestions([]);
        setAnswers({});
        setResult(null);
        setCollapsed(false);
    }, [level, bcsTerm, division, unit]);

    // Re-clicking the "Daily Quick Quiz" tab while it's already active bumps restartSignal —
    // dismiss the report card (or an in-progress set) and go back to the start screen so a
    // fresh quiz can begin. Skipped on mount (signal starts at 0).
    useEffect(() => {
        if (!restartSignal) return;
        setPhase('idle');
        setQuestions([]);
        setAnswers({});
        setResult(null);
        setCollapsed(false);
    }, [restartSignal]);

    const handleStart = async () => {
        if (!level || limitReached) return;
        setLoading(true);
        const url = getPublicQuizQuestionsUrl(level, QUIZ_COUNT, bcsTerm || '', { division, unit });
        const response = await CallApi({ url, method: 'GET', token: false });
        setLoading(false);
        if (response.success) {
            setQuestions(response.data.questions || []);
            setAnswers({});
            setResult(null);
            setCollapsed(false);
            setPhase('in_progress');
        }
    };

    const handleSelectOption = (questionId, option) => {
        setAnswers(prev => ({ ...prev, [questionId]: option }));
    };

    // Multi-select (multiple_correct MCQ + Sentence Equivalence): toggle a letter in an array.
    const handleToggleOption = (questionId, option) => {
        setAnswers(prev => {
            const cur = Array.isArray(prev[questionId]) ? prev[questionId] : [];
            const next = cur.includes(option) ? cur.filter(o => o !== option) : [...cur, option];
            return { ...prev, [questionId]: next };
        });
    };

    // Text Completion: one choice index per blank, stored as { blanks: { blankIdx: choiceIdx } }.
    const handleSelectBlank = (questionId, blankIdx, choiceIdx) => {
        setAnswers(prev => {
            const cur = (prev[questionId] && prev[questionId].blanks) || {};
            const nextBlanks = { ...cur, [blankIdx]: choiceIdx };
            return { ...prev, [questionId]: { blanks: nextBlanks } };
        });
    };

    // TEXT / IELTS short-answer: free text, stored as { text }.
    const handleTextChange = (questionId, text) => {
        setAnswers(prev => ({ ...prev, [questionId]: { text } }));
    };

    // Build a Sentence: ordered tile indices, stored as { order }.
    const handleOrderChange = (questionId, order) => {
        setAnswers(prev => ({ ...prev, [questionId]: { order } }));
    };

    const handleSubmit = async () => {
        // Build answers in the same shape the full quiz taker submits (handles MCQ/multi/TC/
        // build-sentence/matching/TEXT), so grading matches the real quiz. We include EVERY
        // question (not just answered ones) — unanswered questions are sent with an empty
        // selection so the grader marks them skipped/incorrect and the report card covers the
        // whole set. This also means submitting with no/partial answers is allowed (no
        // "answers must not be empty" block, since the full question set is always sent).
        const answered = buildSelectedOptionsJson(answers);
        const answeredById = new Map(answered.map(e => [e.question_id, e]));
        const attemptAnswers = questions.map(q =>
            answeredById.get(q.question_id) || { question_id: q.question_id, selected_option: '' }
        );
        const payload = constructPublicGradePayload(attemptAnswers);
        setLoading(true);
        const response = await CallApi({
            url: getPublicGradeUrl(),
            method: 'POST',
            payload,
            token: false,
        });
        setLoading(false);
        if (response.success) {
            const data = response.data;
            setResult(data);
            setPhase('results');
            // `attemptAnswers` (built above) is kept so the attempt supports full review later
            // (after login / on the Daily Challenge page).
            // Device-level "recent quick quizzes" log for the homepage activity box —
            // kept for BOTH anon and logged-in users (display only; never imported).
            appendRecentQuiz({
                ts: new Date().toISOString(),
                date: anonymousToday(),
                level,
                score_pct: data.score_pct,
                total: data.total,
                correct: data.correct,
            });
            if (!isLoggedIn) {
                appendAnonymousQuiz({
                    date: anonymousToday(),
                    ts: new Date().toISOString(),
                    level,
                    score_pct: data.score_pct,
                    total: data.total,
                    correct: data.correct,
                    answers: attemptAnswers,
                });
                setQuizCountToday(getAnonymousQuizCountToday());
            } else {
                // Logged-in quick quizzes are recorded server-side so they appear under
                // "Daily Quick Quizzes (Random Question & Logged in)" on the Daily Challenge page.
                CallApi({
                    url: getQuickSubmissionUrl(),
                    method: 'POST',
                    payload: constructQuickSubmissionPayload(level, data.correct, data.total, attemptAnswers),
                    token: true,
                }).catch(() => { /* fire-and-forget; grading already shown */ });
                setLoggedInCountToday(c => c + 1);
            }
            // Refresh the homepage activity box immediately.
            if (onActivity) onActivity();
        }
    };

    const handleRetake = () => {
        setPhase('idle');
        setQuestions([]);
        setAnswers({});
        setResult(null);
    };

    if (phase === 'idle') {
        if (limitReached) {
            return (
                <div className="card shadow-sm">
                    <div className="card-body text-center py-5">
                        <h3 className="card-title mb-3">Daily quick test limit reached</h3>
                        <p className="text-muted mb-4">
                            {limitReachedMessage({ kind: 'quick', isLoggedIn, isPremium, limit: dailyLimit })}
                        </p>
                        {isLoggedIn ? (
                            <UpgradeNudge suffix="for more daily tests" />
                        ) : (
                            <>
                                <Link to="/signup" className="nav-pill me-2">Sign Up</Link>
                                <Link to="/login" className="nav-pill">Login</Link>
                            </>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                    <h3 className="card-title mb-3">Take a 15-question test</h3>
                    <p className="text-muted mb-4">
                        Random questions from the {level || '—'} bank. No timer, no signup needed.
                    </p>
                    {remaining != null && (
                        <p className="mb-3">
                            <span className="badge bg-info-subtle text-dark">
                                {remaining} of {dailyLimit} quick {dailyLimit === 1 ? 'test' : 'tests'} left today
                            </span>
                        </p>
                    )}
                    <button
                        className="nav-pill"
                        style={{ padding: '0.6rem 1.6rem', fontSize: '1.05rem', opacity: (!level || loading) ? 0.6 : 1 }}
                        onClick={handleStart}
                        disabled={!level || loading}
                    >
                        {loading ? 'Loading…' : 'Start Test'}
                    </button>
                    {!level && (
                        <div className="text-danger fw-semibold mt-3">
                            <small>Pick a level above to start.</small>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (phase === 'results') {
        return (
            <LandingReportCard
                result={result}
                level={level}
                onJumpToPractice={onJumpToPractice}
                onRetake={handleRetake}
            />
        );
    }

    // in_progress
    const answeredCount = questions.filter(q => answers[q.question_id]).length;

    return (
        <div className="card shadow-sm">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3 sticky-top bg-white py-2"
                    style={{ top: '76px', borderBottom: '1px solid #eee', zIndex: 5 }}>
                    <strong>{level} test — {answeredCount}/{questions.length} answered</strong>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-success"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Submitting…' : 'Submit My Answers'}
                        </button>
                        <button
                            className="btn btn-sm"
                            style={{ backgroundColor: '#7c3aed', color: '#fff', border: 'none' }}
                            onClick={() => setCollapsed(c => !c)}
                            title={collapsed ? 'Expand questions' : 'Collapse questions'}
                        >
                            {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            <span className="ms-1">{collapsed ? 'Expand' : 'Collapse'}</span>
                        </button>
                    </div>
                </div>
                {!collapsed && (
                    <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
                        {questions.map((q, idx) => (
                            <QuizQuestionCard
                                key={q.question_id}
                                question={q}
                                qIndex={idx}
                                includePassage
                                answer={answers[q.question_id]}
                                isAnswered={answers[q.question_id] != null}
                                onOptionSelect={handleSelectOption}
                                onMultiToggle={handleToggleOption}
                                onBlankSelect={handleSelectBlank}
                                onTextChange={handleTextChange}
                                onOrderChange={handleOrderChange}
                            />
                        ))}
                        <div className="text-center pt-3">
                            <button
                                className="btn btn-success btn-lg"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Submitting…' : 'Submit My Answers'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LandingQuickQuiz;
